import { Capacitor, registerPlugin } from '@capacitor/core';

import { isStoreBuild } from './platform';

/**
 * Предупреждение о снимке экрана — iOS-приложение (D62, решение владельца; 26.09.2026, сборка 61).
 *
 * iOS не даёт запретить снимок: о нём сообщают уже после (`userDidTakeScreenshotNotification`). Запись экрана
 * закрывает шторка в самом приложении (`ios/App/App/ScreenGuardPlugin.swift`), а на снимок — вежливое
 * предупреждение: материалы уроков только для личной учёбы. На Android снимок и запись не даёт система
 * (FLAG_SECURE в `MainActivity.java`), там это не нужно. В браузере — ничего (сторож `screenGuard.test.ts`).
 *
 * Показ — как у всплывашки «Нет сети»: одна на экране, сама уходит через `SHOW_MS`.
 */

type ScreenGuardPlugin = {
  addListener(event: 'screenshot', cb: () => void): Promise<{ remove: () => Promise<void> }>;
};

const ScreenGuard = registerPlugin<ScreenGuardPlugin>('ScreenGuard');

export const SHOW_MS = 5_000;

type Listener = (visible: boolean) => void;

let visible = false;
let hideTimer: ReturnType<typeof setTimeout> | undefined;
const listeners = new Set<Listener>();

function emit(): void {
  listeners.forEach((l) => {
    try {
      l(visible);
    } catch {
      /* один сломавшийся слушатель не должен мешать остальным */
    }
  });
}

/** Показать предупреждение (зовёт слушатель плагина). Повторный снимок продлевает показ. */
export function showScreenshotNotice(): void {
  visible = true;
  if (hideTimer) clearTimeout(hideTimer);
  hideTimer = setTimeout(dismissScreenshotNotice, SHOW_MS);
  emit();
}

export function dismissScreenshotNotice(): void {
  if (hideTimer) clearTimeout(hideTimer);
  hideTimer = undefined;
  if (!visible) return;
  visible = false;
  emit();
}

export const isScreenshotNoticeVisible = (): boolean => visible;

export function onScreenshotNotice(l: Listener): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

let started = false;

/** Подписаться на снимки экрана. Только iOS-приложение; в браузере и на Android — ничего. */
export function initScreenGuard(): void {
  if (started || !isStoreBuild() || Capacitor.getPlatform() !== 'ios') return;
  started = true;
  ScreenGuard.addListener('screenshot', showScreenshotNotice).catch((err: unknown) => {
    // Плагина нет (старая родная часть) — предупреждения не будет, шторка записи от этого не зависит.
    console.error('[ScreenGuard] listener failed', err);
  });
}
